import { useEffect, useId } from "react";
import type { ReactNode } from "react";

// App-wide modal primitive. Blur-material backdrop, ESC + backdrop-click
// to close. Title sits above the inner padding; footer holds actions.

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
  /**
   * Accessible name for dialogs that render their own heading in `children` instead of passing
   * `title` (the agent sheet does). Ignored when `title` is set — that path labels itself.
   */
  ariaLabel?: string;
  // Spacious dialog for data-dense content (e.g. the LinkedIn import review): it
  // grows toward the viewport but deliberately KEEPS a backdrop margin + blur so
  // the platform stays visible behind it — the user knows they're still in Smycles,
  // not on a separate page. Body scrolls between a pinned title and footer.
  large?: boolean;
}

export function Modal({ open, onClose, title, children, footer, maxWidth = "32rem", large = false, ariaLabel }: ModalProps) {
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/30 backdrop-blur-md" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        // role="dialog" with no name is announced as just "dialog". Prefer the visible title,
        // fall back to an explicit label for dialogs that head themselves inside `children`.
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        className={
          large
            // Large but bounded: max-h-[90vh] keeps a top/bottom gap so the blurred
            // platform stays visible; body is the single flex-growing scroll region so
            // the footer stays pinned. Width cap is set via style (below).
            ? "relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-md bg-surface shadow-xl ring-1 ring-ink/10"
            : "relative max-h-[85vh] w-full overflow-auto rounded-md bg-surface shadow-xl ring-1 ring-ink/10"
        }
        // Large: cap the width so the left/right gap to the viewport is HALF of the
        // former 72rem-centered margin — (100vw − W)/2 = ((100vw − 72rem)/2)/2 when
        // W = (100vw + 72rem)/2. Top/bottom keep the max-h-[90vh] gap. Windowed: fixed.
        style={large ? { maxWidth: "calc((100vw + 72rem) / 2)" } : { maxWidth }}
      >
        {title ? (
          <div
            id={titleId}
            className={`px-6 pb-3 pt-5 text-[10px] tracking-[0.25em] text-ink/55 ${large ? "shrink-0" : ""}`}
          >
            {title}
          </div>
        ) : null}
        {/* Large: the body is the single flex-growing, scrolling region so the footer
            stays pinned; windowed: the whole box scrolls as before. */}
        <div className={large ? "flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6" : "px-6 pb-6"}>
          {children}
        </div>
        {footer ? (
          <div className={`flex justify-end gap-2 border-t border-ink/5 px-6 py-4 ${large ? "shrink-0" : ""}`}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
